import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { useNotify } from '../../../hooks';
import { Plan } from '../../../models';
import { CREATE_PLAN, UPDATE_PLAN } from '../../../graphql/mutation';
import { validatePlanForm } from '../../../lib/formValidations';
import { FormButtons } from '../../shared/Forms';
import PlanFormContent from './PlanFormContent';

interface PlanFormProps {
  plan?: Plan;
}

function PlanForm({ plan }: PlanFormProps) {
  const notify = useNotify();
  const router = useRouter();

  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<Plan>>(
    plan
      ? {
          ...plan,
          frequencies: plan.frequencies.map((f) => ({
            time: f.time,
            price: f.price,
          })),
        }
      : {
          title: '',
          freeCoupons: 0,
          couponPrice: 0,
          discountCoupons: 0,
          frequencies: [{}],
        }
  );

  const [createPlanMutation] = useMutation<{
    createPlan: Plan;
  }>(CREATE_PLAN);

  const [updatePlanMutation] = useMutation<{
    updatePlan: Plan;
  }>(UPDATE_PLAN);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled) return;
    try {
      NProgress.start();
      setDisabled(true);

      const error = validatePlanForm(formState);
      if (error) {
        return notify(error, 'error');
      }

      const planData = {
        ...formState,
        freeCoupons: Number(formState.freeCoupons),
        couponPrice: Number(formState.couponPrice),
        discountCoupons: Number(formState.discountCoupons),
        frequencies: formState.frequencies.map((f) => ({
          time: Number(f.time),
          price: Number(f.price),
          __typename: undefined,
        })),
        __typename: undefined,
      };

      if (!plan?._id) {
        createPlan(planData);
      } else {
        updatePlan(planData);
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createPlan = async (planData: Partial<Plan>) => {
    try {
      const { data } = await createPlanMutation({
        variables: {
          data: {
            ...planData,
          },
        },
      });
      if (data?.createPlan?._id) {
        notify('El plan se ha creado con éxito', 'success');
        router.push('/app/maintenance/plans');
      } else {
        notify('Ha ocurrido un error al crear el plan', 'error');
      }
    } catch (error) {
      return notify(error.message, 'error');
    }
  };

  const updatePlan = async (planData: Partial<Plan>) => {
    try {
      const { data } = await updatePlanMutation({
        variables: {
          data: {
            ...planData,
            _id: plan?._id,
            frequencies: planData.frequencies.map((f) => ({
              time: Number(f.time),
              price: Number(f.price),
              __typename: undefined,
            })),
            __typename: undefined,
          },
        },
      });
      if (data?.updatePlan?._id) {
        notify('Plan actualizado con éxito', 'success');
        router.push('/app/maintenance/plans');
      } else {
        notify('Ha ocurrido un error al actualizar la categoría', 'error');
      }
    } catch (error) {
      return notify(error.message, 'error');
    }
  };

  const toggleActive = () => updatePlan({ ...formState, active: !plan.active });

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <PlanFormContent formState={formState} setFormState={setFormState} />
      <FormButtons
        type={plan ? 'update' : 'create'}
        toggleActive={toggleActive}
        active={plan?.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default PlanForm;
